import { action, makeObservable, observable, computed, autorun, reaction } from 'mobx';
import { marketType } from '../enums/marketType';
import { CHIP_COLORS } from '../enums/chipColor';
import { BetType } from '../enums/tableNumbers';
import { parseNumberByPrecision } from '@pascal/pascal-core';
import { getKeyByValue } from '../helpers/getKeyByValue';
import { applicationStore, freeBetStore, gameConfigStore } from '@pascal/pascal-core/shared/core/store';
import { mathConfig, mathJS } from '../helpers/mathFormat';
import { gameStore } from './gameStore';
import { allChipsTotal } from '../helpers/chipsTotal';

class DoChipStore {
  selectedChip = 10;
  allBetsModels = [];
  demoAllBetsModels = [];
  neighbors = 2;
  quickBets = [];
  reBet = null;

  constructor() {
    makeObservable(this, {
      selectedChip: observable,
      neighbors: observable,
      quickBets: observable,
      demoAllBetsModels: observable,
      reBet: observable,
      allBetsModels: observable,
      setSelectedChip: action.bound,
      setReBet: action.bound,
      resetReBet: action.bound,
      setQuickBets: action.bound,
      setNeighbors: action.bound,
      setDoBet: action.bound,
      setUndoBet: action.bound,
      setDoubleBet: action.bound,
      setDoBetInCombination: action.bound,
      handleChangeAllBetsModels: action.bound,
      setAllBetsModel: action.bound,
      setReBetInitial: action.bound,
      resetForFavoriteBets: action.bound,
      getAllBetsModels: computed,
      betsTotalAmount: computed,
      limitsByBetType: computed,
    });

    reaction(
      () => this.betsTotalAmount,
      (value, previousValue) => {
        if (previousValue !== value) {
          // iframeService.sendStakeUpdate(value || 0);
        }
      }
    );

    autorun(() => {
      if (freeBetStore.activeFreeBet.id) {
        this.setSelectedChip(freeBetStore.activeFreeBet.amount);
      }
    });

    reaction(
      () => gameStore.isBetsClosed,
      (value) => {
        freeBetStore.setIsAllowChangeFreeBet(!value);
      }
    );

    reaction(
      () => freeBetStore.activeFreeBet.id,
      (value, previousValue) => {
        if (previousValue && !value) {
          const { limits } = gameConfigStore;
          this.resetAllBetsModels();
          this.setReBet(null);
          this.setSelectedChip(limits?.quickBets?.[0]);
        }
        if (previousValue !== value) {
          this.resetAllBetsModels();
          this.setReBet(null);
        }
      }
    );
  }

  get getAllBetsModels() {
    if (applicationStore.isDemo) {
      return this.demoAllBetsModels;
    }
    return this.allBetsModels;
  }

  get betsTotalAmount() {
    return allChipsTotal(this.getAllBetsModels[0]) || 0;
  }

  setSelectedChip(value) {
    // if (value) {
    //   iframeService.sendStakeUpdate(value);
    // }
    this.selectedChip = value;
  }

  setReBet() {
    this.reBet = {
      lastBetModel: this.getAllBetsModels[0],
    };
  }

  setReBetInitial(betModel) {
    this.reBet = {
      lastBetModel: { betModel },
    };
  }

  resetReBet() {
    this.reBet = null;
  }

  setAllBetsModel(payload, notDemo) {
    if (notDemo) {
      this.allBetsModels = payload;
      return;
    }
    if (applicationStore.isDemo) {
      this.demoAllBetsModels = payload;
    } else {
      this.allBetsModels = payload;
    }
  }

  resetAllBetsModels(withOutRealBets) {
    if (withOutRealBets) {
      this.demoAllBetsModels = [];
    } else {
      this.demoAllBetsModels = [];
      this.allBetsModels = [];
    }
  }

  resetForFavoriteBets(length) {
    if (applicationStore.isDemo) {
      this.demoAllBetsModels.splice(1, length - 1);
    } else {
      this.allBetsModels.splice(1, length - 1);
    }
  }

  setQuickBets(quickBets) {
    this.quickBets = [...quickBets].map((chip, index) => ({
      chip,
      color: CHIP_COLORS[index],
    }));
  }

  get limitsByBetType() {
    const {
      limits: { additionalLimits },
    } = gameConfigStore;
    const { activeFreeBet } = freeBetStore;

    if (activeFreeBet.id) {
      const newLimits = { ...additionalLimits };
      Object.keys(additionalLimits).forEach((key) => {
        newLimits[key].maxBet = activeFreeBet.amount;
      });
      return newLimits;
    }

    return additionalLimits;
  }

  setNeighbors(mathMethod) {
    if (mathMethod === 'inc' && this.neighbors <= 8) {
      this.neighbors += 1;
    } else if (mathMethod === 'dec' && this.neighbors) {
      this.neighbors -= 1;
    }
  }

  handleChangeAllBetsModels(isDoubleChips, chipData, betType) {
    let allBets;
    const lastBetModel = this.getAllBetsModels[0] || [];

    if (isDoubleChips) {
      const betModel = lastBetModel.betModel.map((item) => {
        if (item?.includedBets) {
          return {
            bet: { ...item.bet },
            isDouble: true,
            includedBets: item.includedBets.map((betsItem) => {
              const itemAmount = isDoubleChips.find(
                (bet) =>
                  bet.type === getKeyByValue(marketType, betsItem.type) &&
                  bet.betType === getKeyByValue(marketType, item.bet.type)
              );
              return {
                ...betsItem,
                amount: itemAmount[itemAmount.type],
              };
            }),
          };
        }
        const itemAmount = isDoubleChips.find(
          (bet) => bet.type === getKeyByValue(marketType, item.bet.type) && !bet?.betType
        );
        return { bet: { ...item.bet, amount: itemAmount[itemAmount.type] } };
      });

      return {
        betModel,
      };
    }
    if (!betType) {
      const isTypeInLastBetModel = lastBetModel?.betModel?.some((item) => item.bet.type === marketType[chipData.type]);
      if (isTypeInLastBetModel) {
        allBets = {
          betModel: lastBetModel?.betModel.map((item) => {
            if (item.bet.type === marketType[chipData.type]) {
              return this.createBetModelByNumber(chipData);
            }
            return item;
          }),
        };
      } else {
        allBets = {
          betModel: [this.createBetModelByNumber(chipData), ...(lastBetModel?.betModel || [])],
        };
      }
      return allBets;
    } else {
      const isTypeInLastBetModel = lastBetModel?.betModel?.some((item) => item.bet.type === marketType[betType]);
      if (isTypeInLastBetModel) {
        allBets = {
          betModel: lastBetModel?.betModel.map((item) => {
            if (item.bet.type === marketType[betType]) {
              if (betType.includes(BetType.Neighbors)) {
                return {
                  bet: {
                    type: marketType[betType],
                    count: this.neighbors,
                  },
                  includedBets: item.includedBets.map(({ type }) => {
                    const itemAmount = chipData.find((bet) => bet.type === getKeyByValue(marketType, type));
                    return {
                      type,
                      amount: parseNumberByPrecision(1, +mathJS.format(itemAmount[itemAmount.type], mathConfig)),
                    };
                  }),
                };
              }
              return {
                bet: {
                  type: marketType[betType],
                },
                includedBets: item.includedBets.map(({ type }) => {
                  const itemAmount = chipData.find((bet) => bet.type === getKeyByValue(marketType, type));
                  return {
                    type,
                    amount: parseNumberByPrecision(1, +mathJS.format(itemAmount[itemAmount.type], mathConfig)),
                  };
                }),
              };
            }
            return item;
          }),
        };
      } else {
        allBets = {
          betModel: [
            this.createBetModelByCombination(chipData, betType, this.selectedChip),
            ...(lastBetModel?.betModel || []),
          ],
        };
      }
      return allBets;
    }
  }

  createBetModelByNumber(model, isCombination) {
    if (isCombination) {
      return {
        type: marketType[model.type],
        amount: model[model.type],
      };
    }
    return {
      bet: {
        type: marketType[model.type],
        amount: model[model.type],
      },
    };
  }

  createBetModelByCombination(model, type) {
    if (type.includes(BetType.Neighbors)) {
      return {
        bet: {
          type: marketType[type],
          count: this.neighbors,
        },
        includedBets: model.map((item) => this.createBetModelByNumber(item, true)),
      };
    }
    return {
      bet: {
        type: marketType[type],
      },
      includedBets: model.map((item) => this.createBetModelByNumber(item, true)),
    };
  }

  setDoBet(chipData, betType) {
    if (!betType) {
      const resultBetModel = this.handleChangeAllBetsModels(false, chipData);
      if (applicationStore.isDemo) {
        this.demoAllBetsModels = [resultBetModel, ...this.demoAllBetsModels];
      } else {
        this.allBetsModels = [resultBetModel, ...this.allBetsModels];
      }
    }
  }

  setUndoBet() {
    if (applicationStore.isDemo) {
      const newBetModel = [...this.demoAllBetsModels];
      newBetModel.shift();
      this.demoAllBetsModels = newBetModel;
    } else {
      const newBetModel = [...this.allBetsModels];
      newBetModel.shift();
      this.allBetsModels = newBetModel;
    }
  }

  setDoBetInCombination({ combinations, betType }) {
    const resultBetModel = this.handleChangeAllBetsModels(false, combinations, betType);
    if (applicationStore.isDemo) {
      this.demoAllBetsModels = [resultBetModel, ...this.demoAllBetsModels];
    } else {
      this.allBetsModels = [resultBetModel, ...this.allBetsModels];
    }
  }

  setDoubleBet({ combinations }) {
    const resultBetModel = this.handleChangeAllBetsModels(combinations);
    if (applicationStore.isDemo) {
      this.demoAllBetsModels = [resultBetModel, ...this.demoAllBetsModels];
    } else {
      this.allBetsModels = [resultBetModel, ...this.allBetsModels];
    }
  }
}

export const doChipStore = new DoChipStore();
